import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, ArrowRight, ShieldCheck, CalendarPlus, Sparkles, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Chrome-less, brand-themed coaching-package menu designed to be embedded via iframe.
 * URL: /embed/packages?brand=<slug>
 * Each package: "Enroll & Pay" (Stripe one-time checkout, opens in a new tab) +
 * "Book a Free Strategy Call" (-> /embed/schedule scheduler, consultation funnel).
 */
export default function EmbedPackagesPage() {
  const [params] = useSearchParams();
  const brandSlug = params.get('brand') || 'dca';
  const [brand, setBrand] = useState(null);
  const [enrolling, setEnrolling] = useState(null); // package object being enrolled
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.body.style.background = 'transparent';
    axios.get(`${API_URL}/api/booking-brands/${brandSlug}`)
      .then((r) => setBrand(r.data))
      .catch(() => toast.error('Brand not found. Please contact support.'));
  }, [brandSlug]);

  if (!brand) {
    return (
      <div className="min-h-[400px] flex items-center justify-center bg-transparent">
        <div className="w-10 h-10 border-4 border-[#94A3B8] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const theme = {
    primary: brand.primary_color || '#1D4ED8',
    accent: brand.accent_color || '#F97316',
    bg: brand.bg_color || '#0F172A',
  };
  const packages = (brand.packages || []).filter((p) => p.is_active !== false)
    .sort((a, b) => (a.order || 0) - (b.order || 0));
  const freeService = (brand.services || []).find((s) => Number(s.price) === 0);
  const callUrl = `/embed/schedule?brand=${brand.slug}` + (freeService ? `&service=${freeService.service_id}` : '');

  const startEnroll = (pkg) => { setEnrolling(pkg); setForm({ name: '', email: '', phone: '' }); };

  const submitEnroll = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.includes('@')) { toast.error('Name and a valid email are required'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/coaching-packages/checkout`, {
        brand_slug: brand.slug,
        package_id: enrolling.package_id,
        name: form.name, email: form.email, phone: form.phone,
        origin_url: window.location.origin,
      });
      // Stripe Checkout cannot run inside an iframe -> open in a new tab.
      window.open(res.data.url, '_blank', 'noopener');
      toast.success('Secure checkout opened in a new tab');
      setEnrolling(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start checkout');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: '#FAFAFA' }} data-testid="embed-packages-page">
      {/* Brand hero */}
      <div style={{ background: theme.bg, color: '#fff' }} className="px-5 py-8 sm:px-8 sm:py-10 text-center">
        {brand.logo_url && <img src={brand.logo_url} alt={brand.name} className="h-12 mx-auto mb-3 object-contain" />}
        <div className="text-[11px] font-bold tracking-[2px] uppercase mb-2" style={{ color: theme.accent }}>{brand.name}</div>
        <h1 className="text-2xl sm:text-3xl font-bold mb-2" data-testid="embed-packages-title">Coaching Paths</h1>
        <p className="text-sm" style={{ color: '#CBD5E1' }}>Choose the partnership that matches your goals — or start with a free strategy call.</p>
      </div>

      <div className="px-4 sm:px-8 py-8 max-w-5xl mx-auto">
        {packages.length === 0 ? (
          <p className="text-center text-[#475569]">No coaching packages available right now.</p>
        ) : (
          <div className="grid gap-5 md:grid-cols-3">
            {packages.map((p, i) => (
              <div
                key={p.package_id}
                className="bg-white border rounded-2xl p-6 flex flex-col"
                style={{ borderColor: i === 1 ? theme.accent : '#E2E8F0', borderWidth: i === 1 ? 2 : 1 }}
                data-testid={`embed-package-${p.package_id}`}
              >
                {i === 1 && (
                  <div className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full self-start mb-3" style={{ background: theme.accent + '22', color: theme.accent }}>
                    Most popular
                  </div>
                )}
                <h3 className="font-bold text-[#0A0F1C] text-lg">{p.name}</h3>
                <div className="text-xs text-[#94A3B8] mb-3">{p.duration_label}</div>
                <div className="flex items-baseline gap-1 mb-3">
                  <span className="text-3xl font-bold" style={{ color: theme.primary }}>${Number(p.price).toLocaleString()}</span>
                  <span className="text-xs text-[#94A3B8]">one-time</span>
                </div>
                <p className="text-sm text-[#475569] mb-4">{p.description}</p>
                <ul className="space-y-2 mb-6 flex-1">
                  {(p.features || []).map((f, fi) => (
                    <li key={fi} className="flex items-start gap-2 text-sm text-[#334155]">
                      <Check size={15} className="mt-0.5 shrink-0" style={{ color: theme.primary }} /> {f}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => startEnroll(p)}
                  className="w-full font-bold text-sm py-2.5 rounded-lg text-white inline-flex items-center justify-center gap-2 transition-opacity hover:opacity-90"
                  style={{ background: theme.primary }}
                  data-testid={`embed-enroll-${p.package_id}`}
                >
                  Enroll Now — ${Number(p.price).toLocaleString()} <ArrowRight size={15} />
                </button>
                <a
                  href={callUrl}
                  className="w-full font-semibold text-sm py-2.5 rounded-lg inline-flex items-center justify-center gap-2 mt-2 border hover:bg-[#F8FAFC]"
                  style={{ borderColor: '#E2E8F0', color: theme.primary }}
                  data-testid={`embed-callcta-${p.package_id}`}
                >
                  <CalendarPlus size={15} /> Book a Free Strategy Call
                </a>
              </div>
            ))}
          </div>
        )}

        <div className="mt-8 flex items-center justify-center gap-2 text-xs text-[#94A3B8]">
          <ShieldCheck size={14} /> Secure checkout by Stripe · Not sure yet? <a href={callUrl} className="font-bold" style={{ color: theme.primary }}>Start with a free strategy call</a>
        </div>
      </div>

      {/* Enroll modal */}
      {enrolling && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="embed-enroll-modal">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 relative">
            <button onClick={() => setEnrolling(null)} className="absolute top-4 right-4 text-[#94A3B8] hover:text-[#0A0F1C]" aria-label="Close"><X size={18} /></button>
            <div className="flex items-center gap-2 mb-1"><Sparkles size={16} style={{ color: theme.accent }} /><span className="text-xs font-bold uppercase tracking-wide" style={{ color: theme.accent }}>{enrolling.duration_label}</span></div>
            <h3 className="text-xl font-bold text-[#0A0F1C] mb-1">{enrolling.name}</h3>
            <div className="text-2xl font-bold mb-4" style={{ color: theme.primary }}>${Number(enrolling.price).toLocaleString()}</div>
            <form onSubmit={submitEnroll} className="space-y-3">
              <input type="text" placeholder="Full Name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-lg text-sm focus:outline-none" data-testid="embed-enroll-name" />
              <input type="email" placeholder="Email Address" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-lg text-sm focus:outline-none" data-testid="embed-enroll-email" />
              <input type="tel" placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full px-3 py-2.5 border border-[#E2E8F0] rounded-lg text-sm focus:outline-none" data-testid="embed-enroll-phone" />
              <button type="submit" disabled={submitting} className="w-full font-bold text-sm py-2.5 rounded-lg text-white inline-flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: theme.primary }} data-testid="embed-enroll-submit">
                <ShieldCheck size={15} /> {submitting ? 'Opening checkout…' : 'Continue to Secure Checkout'}
              </button>
              <p className="text-[11px] text-[#94A3B8] text-center">You&apos;ll complete payment securely via Stripe in a new tab.</p>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
